/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { withEuiTheme } from '../../services';
import { EuiFlexGroup, EuiFlexItem } from '../flex';
import { EuiIcon } from '../icon';
import { euiFilterSelectItemStyles } from './filter_select_item.styles';
var resolveIconAndColor = function resolveIconAndColor(checked) {
  if (!checked) {
    return {
      icon: 'empty'
    };
  }
  return checked === 'on' ? {
    icon: 'check',
    color: 'text'
  } : {
    icon: 'cross',
    color: 'text'
  };
};

/**
 * TODO: This component should removed in favor of EuiSelectable usage
 * once EuiComboBox has been converted to dogfood EuiSelectable.
 *
 * @deprecated - Use EuiSelectable instead
 */
export class EuiFilterSelectItemClass extends Component {
  static defaultProps = {
    showIcons: true
  };
  buttonRef = null;
  state = {
    hasFocus: false
  };
  focus = () => {
    if (this.buttonRef) {
      this.buttonRef.focus();
    }
  };
  hasFocus = () => {
    return this.state.hasFocus;
  };
  render() {
    const {
      theme,
      children,
      className,
      disabled,
      checked,
      isFocused,
      showIcons,
      ...rest
    } = this.props;
    const styles = euiFilterSelectItemStyles(theme);
    const cssStyles = [styles.euiFilterSelectItem, isFocused && styles.isFocused];
    const classes = classNames('euiFilterSelectItem', className);
    let iconNode;
    if (showIcons) {
      const {
        icon,
        color
      } = resolveIconAndColor(checked);
      iconNode = <EuiFlexItem grow={false}>
          <EuiIcon color={color} type={icon} />
        </EuiFlexItem>;
    }
    return <button ref={ref => this.buttonRef = ref} role="option" type="button" aria-selected={isFocused} className={classes} css={cssStyles} disabled={disabled} aria-disabled={disabled} {...rest}>
        <EuiFlexGroup alignItems="center" gutterSize="s" component="span" responsive={false}>
          {iconNode}
          <EuiFlexItem className="euiFilterSelectItem__content eui-textTruncate" component="span">
            {children}
          </EuiFlexItem>
        </EuiFlexGroup>
      </button>;
  }
}

/**
 * @deprecated - Use EuiSelectable instead
 */
EuiFilterSelectItemClass.propTypes = {
  checked: PropTypes.oneOf(["on", "off"]),
  showIcons: PropTypes.bool,
  isFocused: PropTypes.bool,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
export const EuiFilterSelectItem = withEuiTheme(EuiFilterSelectItemClass);
try {
  EuiFilterSelectItemClass.__docgenInfo = {
    tags: {
      deprecated: '- Use EuiSelectable instead'
    },
    filePath: '/app/src/components/filter_group/filter_select_item.tsx',
    description: 'TODO: This component should removed in favor of EuiSelectable usage\n' + 'once EuiComboBox has been converted to dogfood EuiSelectable.',
    displayName: 'EuiFilterSelectItemClass',
    methods: [],
    props: {
      theme: {
        defaultValue: null,
        description: '',
        name: 'theme',
        parent: {
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        },
        declarations: [{
          fileName: 'app/src/services/theme/hooks.tsx',
          name: 'WithEuiThemeProps'
        }],
        required: true,
        type: {
          name: 'UseEuiTheme<{}>'
        }
      },
      checked: {
        defaultValue: null,
        description: '',
        name: 'checked',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'FilterChecked',
          value: [{
            value: '"off"'
          }, {
            value: '"on"'
          }]
        }
      },
      showIcons: {
        defaultValue: {
          value: 'true'
        },
        description: '',
        name: 'showIcons',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isFocused: {
        defaultValue: null,
        description: '',
        name: 'isFocused',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['WithEuiThemeProps', 'EuiFilterSelectItemProps', 'CommonProps', 'ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}
try {
  EuiFilterSelectItem.__docgenInfo = {
    tags: {
      deprecated: '- Use EuiSelectable instead'
    },
    filePath: '/app/src/components/filter_group/filter_select_item.tsx',
    description: '',
    displayName: 'EuiFilterSelectItem',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@emotion/react/types/css-prop.d.ts',
          name: 'Attributes'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      checked: {
        defaultValue: null,
        description: '',
        name: 'checked',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'FilterChecked',
          value: [{
            value: '"off"'
          }, {
            value: '"on"'
          }]
        }
      },
      isFocused: {
        defaultValue: null,
        description: '',
        name: 'isFocused',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      showIcons: {
        defaultValue: null,
        description: '',
        name: 'showIcons',
        parent: {
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        },
        declarations: [{
          fileName: 'app/src/components/filter_group/filter_select_item.tsx',
          name: 'EuiFilterSelectItemProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      ref: {
        defaultValue: null,
        description: 'Allows getting a ref to the component instance.\n' + 'Once the component unmounts, React will set `ref.current` to `null` (or call the ref with `null` if you passed a callback ref).\n' + '@see https://react.dev/learn/referencing-values-with-refs#refs-and-the-dom',
        name: 'ref',
        parent: {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'RefAttributes'
        }],
        required: false,
        type: {
          name: 'Ref<unknown>'
        }
      }
    },
    extendedInterfaces: ['WithEuiThemeProps', 'EuiFilterSelectItemProps', 'CommonProps', 'ButtonHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'Attributes']
  };
} catch (e) {}